import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import { DatePicker } from '@/components/DatePicker'

type Currency = { id: number; code: string; name: string; is_base: boolean; is_active: boolean }

type ExchangeRate = {
  id: number
  from_currency: number
  from_currency_code: string
  to_currency: number
  to_currency_code: string
  rate: string
  effective_date: string
  source: 'MANUAL' | 'ECB' | 'API'
  notes: string
}

const emptyRate = (defaultDate: string) => ({
  from_currency: 0,
  to_currency: 0,
  rate: '1.0',
  effective_date: defaultDate,
  source: 'MANUAL' as ExchangeRate['source'],
  notes: '',
})

export default function FinanceExchangeRates() {
  const qc = useQueryClient()

  const currenciesQ = useQuery({
    queryKey: ['finance', 'currencies'],
    queryFn: async () => (await api.get<Currency[]>('/finance/currencies/')).data,
  })
  const ratesQ = useQuery({
    queryKey: ['finance', 'exchange-rates'],
    queryFn: async () => (await api.get<ExchangeRate[]>('/finance/exchange-rates/')).data,
  })

  const [editing, setEditing] = useState<ExchangeRate | null>(null)
  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (p: any) => (await api.post('/finance/exchange-rates/', p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance', 'exchange-rates'] }); setCreating(false) },
  })
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: any }) =>
      (await api.patch(`/finance/exchange-rates/${id}/`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance', 'exchange-rates'] }); setEditing(null) },
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/exchange-rates/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'exchange-rates'] }),
  })

  const currencies = (currenciesQ.data || []).filter((c) => c.is_active)
  const rates = ratesQ.data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Exchange Rates</h2>
          <p className="text-sm text-gray-500">
            Manual FX rates. The most recent rate on or before a transaction's date is used for conversion.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={currencies.length < 2}>
          + Add rate
        </Button>
      </div>

      <ConverterCard currencies={currencies} />

      {ratesQ.isLoading ? <p className="text-gray-500">Loading…</p> : rates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No rates yet. Add one to enable conversions.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Date</Th>
                <Th>From</Th>
                <Th>To</Th>
                <Th className="text-right">Rate</Th>
                <Th>Source</Th>
                <Th>Notes</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rates.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <Td>{r.effective_date}</Td>
                  <Td className="font-mono">{r.from_currency_code}</Td>
                  <Td className="font-mono">{r.to_currency_code}</Td>
                  <Td className="text-right font-mono">{r.rate}</Td>
                  <Td><span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{r.source}</span></Td>
                  <Td className="text-xs text-gray-500">{r.notes || '—'}</Td>
                  <Td>
                    <button onClick={() => setEditing(r)} className="text-blue-600 hover:underline text-sm mr-3">Edit</button>
                    <button onClick={() => { if (confirm(`Delete rate?`)) deleteMut.mutate(r.id) }}
                      className="text-red-600 hover:underline text-sm">Delete</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <RateDialog
          initial={editing
            ? { from_currency: editing.from_currency, to_currency: editing.to_currency,
                rate: editing.rate, effective_date: editing.effective_date,
                source: editing.source, notes: editing.notes }
            : emptyRate(new Date().toISOString().slice(0, 10))}
          currencies={currencies}
          isEdit={!!editing}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => { setCreating(false); setEditing(null) }}
          onSubmit={(p) => {
            if (editing) updateMut.mutate({ id: editing.id, payload: p })
            else createMut.mutate(p)
          }}
        />
      )}
    </AdminLayout>
  )
}

// ────────── Converter widget ──────────

function ConverterCard({ currencies }: { currencies: Currency[] }) {
  const [from, setFrom] = useState('EUR')
  const [to, setTo] = useState('USD')
  const [amount, setAmount] = useState('100')
  const [onDate, setOnDate] = useState(new Date().toISOString().slice(0, 10))

  const { data, isFetching, error } = useQuery({
    queryKey: ['finance', 'fx-convert', from, to, amount, onDate],
    queryFn: async () => {
      const params = { from, to, amount, date: onDate }
      const r = await api.get('/finance/fx/convert/', { params })
      return r.data
    },
    enabled: !!from && !!to && !!amount,
    retry: false,
  })

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">Currency Converter</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
        <Field>
          <FieldLabel htmlFor="conv-amount">Amount</FieldLabel>
          <Input
            id="conv-amount"
            type="number" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="conv-from">From</FieldLabel>
          <SearchableSelect
            id="conv-from"
            value={from}
            onChange={(v) => setFrom(v)}
            options={currencies.map((c) => ({ value: c.code, label: c.code }))}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="conv-to">To</FieldLabel>
          <SearchableSelect
            id="conv-to"
            value={to}
            onChange={(v) => setTo(v)}
            options={currencies.map((c) => ({ value: c.code, label: c.code }))}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="conv-date">Date</FieldLabel>
          <DatePicker
            id="conv-date" value={onDate}
            onChange={(v) => setOnDate(v)}
          />
        </Field>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Result</label>
          <div className="px-3 py-2 bg-gray-50 rounded font-mono text-sm">
            {isFetching ? '…' : error ? <span className="text-red-600 text-xs">no rate</span> :
              data ? (
                <span>
                  <span className="font-bold">{Number(data.converted_amount).toFixed(2)}</span>
                  <span className="text-gray-500 ml-1">{data.to}</span>
                </span>
              ) : '—'}
          </div>
        </div>
      </div>
      {data && data.rate && (
        <p className="text-xs text-gray-500 mt-2">
          @ 1 {data.from} = {Number(data.rate).toFixed(6)} {data.to} ({data.source} on {data.rate_date})
        </p>
      )}
    </div>
  )
}

function RateDialog({
  initial, currencies, isEdit, onSubmit, onCancel, submitting, error,
}: {
  initial: ReturnType<typeof emptyRate>
  currencies: Currency[]
  isEdit: boolean
  onSubmit: (p: any) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState(() => ({
    ...initial,
    from_currency: initial.from_currency || (currencies[0]?.id ?? 0),
    to_currency: initial.to_currency || (currencies.find((c) => c.is_base)?.id ?? currencies[0]?.id ?? 0),
  }))
  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm((f) => ({ ...f, [k]: v }))
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(form) }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit rate' : 'Add exchange rate'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldLegend>Exchange rate</FieldLegend>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="rate-from">From</FieldLabel>
                  <SearchableSelect
                    id="rate-from"
                    value={form.from_currency ? String(form.from_currency) : undefined}
                    onChange={(v) => set('from_currency', Number(v))}
                    placeholder="— select —"
                    options={currencies.map((c) => ({
                      value: String(c.id), label: c.name, hint: c.code,
                    }))}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="rate-to">To</FieldLabel>
                  <SearchableSelect
                    id="rate-to"
                    value={form.to_currency ? String(form.to_currency) : undefined}
                    onChange={(v) => set('to_currency', Number(v))}
                    placeholder="— select —"
                    options={currencies.map((c) => ({
                      value: String(c.id), label: c.name, hint: c.code,
                    }))}
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="rate-value">Rate (1 from = X to)</FieldLabel>
                <Input
                  id="rate-value"
                  type="number" step="0.00000001" required value={form.rate}
                  onChange={(e) => set('rate', e.target.value)}
                  className="font-mono"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="rate-effective-date">Effective date</FieldLabel>
                <DatePicker
                  id="rate-effective-date" required value={form.effective_date}
                  onChange={(v) => set('effective_date', v)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="rate-source">Source</FieldLabel>
                <Select
                  value={form.source}
                  onValueChange={(v) => set('source', v as any)}
                >
                  <SelectTrigger id="rate-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MANUAL">Manual</SelectItem>
                    <SelectItem value="ECB">European Central Bank</SelectItem>
                    <SelectItem value="API">External API</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="rate-notes">Notes</FieldLabel>
                <Input
                  id="rate-notes"
                  type="text" value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                />
              </Field>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded">
                  {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
                </div>
              )}
            </FieldGroup>
          </FieldSet>

          <div className="flex justify-end gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide ${className || ''}`}>{children}</th>
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
